import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { 
  Search, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Building2,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const Operators = () => {
  const { operators } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'momentum_score', direction: 'desc' });

  const filteredOperators = useMemo(() => {
    let filtered = operators?.filter(op => 
      op.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key] || 0;
      const bVal = b[sortConfig.key] || 0;
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [operators, searchTerm, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Operators
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {filteredOperators.length} operators by momentum
          </p>
        </div>
        <div className="relative w-full sm:w-48 lg:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="operator-search"
            className="pl-8 bg-surface border-white/10 text-xs sm:text-sm h-8"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Accelerating</p>
            <p className="text-xl sm:text-2xl font-heading font-bold text-success mt-0.5 sm:mt-1">
              {operators?.filter(o => o.growth_rate > 5).length || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">&gt;5% growth</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Stable</p>
            <p className="text-xl sm:text-2xl font-heading font-bold text-primary mt-0.5 sm:mt-1">
              {operators?.filter(o => o.growth_rate >= -5 && o.growth_rate <= 5).length || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">-5% to +5%</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Declining</p>
            <p className="text-xl sm:text-2xl font-heading font-bold text-danger mt-0.5 sm:mt-1">
              {operators?.filter(o => o.growth_rate < -5).length || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">&lt;-5% growth</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Avg Score</p>
            <p className="text-xl sm:text-2xl font-heading font-bold text-foreground mt-0.5 sm:mt-1">
              {(operators?.reduce((acc, o) => acc + (o.momentum_score || 0), 0) / (operators?.length || 1)).toFixed(0)}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">0-100</p>
          </CardContent>
        </Card>
      </div>

      {/* Operators Table */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
          <CardTitle className="text-sm font-heading uppercase tracking-widest flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            All Operators
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground uppercase tracking-wider w-12">
                    #
                  </TableHead>
                  <TableHead className="text-xs text-muted-foreground uppercase tracking-wider">
                    Operator
                  </TableHead>
                  <TableHead 
                    className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('momentum_score')}
                  >
                    <span className="flex items-center">
                      Momentum <SortIcon column="momentum_score" />
                    </span>
                  </TableHead>
                  <TableHead 
                    className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                    onClick={() => handleSort('growth_rate')}
                  >
                    <span className="flex items-center">
                      Growth <SortIcon column="growth_rate" />
                    </span>
                  </TableHead>
                  <TableHead 
                    className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground hidden sm:table-cell"
                    onClick={() => handleSort('active_wells')}
                  >
                    <span className="flex items-center">
                      Wells <SortIcon column="active_wells" />
                    </span>
                  </TableHead>
                  <TableHead 
                    className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground hidden md:table-cell"
                    onClick={() => handleSort('total_oil_production')}
                  >
                    <span className="flex items-center">
                      Oil Prod <SortIcon column="total_oil_production" />
                    </span>
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOperators.map((op, idx) => (
                  <TableRow 
                    key={op.id} 
                    data-testid={`operator-row-${idx}`}
                    className="border-white/5 hover:bg-white/[0.02]"
                  >
                    <TableCell className="font-mono text-muted-foreground text-xs">
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-foreground text-sm">
                          {op.name}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono hidden sm:block">
                          {op.normalized_name}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "font-mono text-xs",
                          op.momentum_score >= 70 
                            ? "border-success/50 text-success bg-success/10" 
                            : op.momentum_score >= 40 
                              ? "border-primary/50 text-primary bg-primary/10"
                              : "border-danger/50 text-danger bg-danger/10"
                        )}
                      >
                        {op.momentum_score?.toFixed(0)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className={cn(
                        "flex items-center gap-1 text-sm font-mono",
                        op.growth_rate >= 0 ? "text-success" : "text-danger"
                      )}>
                        {op.growth_rate >= 0 ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {op.growth_rate >= 0 ? '+' : ''}{op.growth_rate?.toFixed(1)}%
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm hidden sm:table-cell">
                      {op.active_wells}
                      <span className="text-muted-foreground text-xs ml-1">
                        / {op.total_wells}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-sm hidden md:table-cell">
                      {formatNumber(op.total_oil_production)} BBL
                    </TableCell>
                    <TableCell>
                      <Link to={`/operators/${op.id}`}>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Operators;
